import type { LoggingConfig } from "./logger.js";
import { DEFAULT_LOGGING_CONFIG } from "./logger.js";
import type { Transport } from "./transport.js";

/**
 * Configuration for EventMsg class
 */
export interface EventMsgConfig<TDevice = unknown> {
  /** Transport instance (handles addressing and connection) */
  transport: Transport<TDevice>;

  /** Maximum message size in bytes after stuffing (default: 4096) */
  maxMessageSize?: number;

  /** Default timeout for operations in milliseconds (default: 5000) */
  messageTimeout?: number;

  /** Text encoding for message data (default: 'utf8') */
  encoding?: "utf8" | "binary";

  /** Logging configuration (replaces debug option) */
  logging?: LoggingConfig;

  /** Protocol-specific options */
  protocol?: ProtocolOptions;
}

/**
 * Protocol-specific configuration options
 */
export interface ProtocolOptions {
  /** Enable strict message validation (default: true) */
  strictValidation?: boolean;

  /** Maximum event name length in bytes (default: 64) */
  maxEventNameLength?: number;

  /** Maximum event data length in bytes (default: 3048) */
  maxEventDataLength?: number;
}

/**
 * Default protocol options
 */
export const DEFAULT_PROTOCOL_OPTIONS: Required<ProtocolOptions> = {
  strictValidation: true,
  maxEventNameLength: 64,
  maxEventDataLength: 3048,
} as const;

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Required<
  Omit<EventMsgConfig<unknown>, "transport">
> = {
  maxMessageSize: 4096,
  messageTimeout: 5000,
  encoding: "utf8",
  logging: DEFAULT_LOGGING_CONFIG,
  protocol: DEFAULT_PROTOCOL_OPTIONS,
} as const;
